// ios TextChunker.swift: article paragraphs → narration parts (progressive sizes), and parts → ≤700-char segments
// (NARRATION.md "Units"). Lengths are JavaScript string lengths; sentences come from Intl.Segmenter (the apps use
// NLTokenizer / BreakIterator), so a part boundary inside a very long paragraph can differ slightly between platforms.
// Parts are synced as text, so every device narrates the same parts whoever chunked them.

export const DEFAULT_MAX = 2500;
export const SEGMENT_LIMIT = 700;

export function progressiveSize(index: number): number {
  return [200, 450, 900, 1600][index] ?? DEFAULT_MAX;
}

const trim = (s: string) => s.trim();

export function chunk(paragraphs: string[], sizeFor: (i: number) => number): string[] {
  const chunks: string[] = [];
  let current = "";
  const flush = () => { const t = trim(current); if (t) chunks.push(t); current = ""; };
  for (const paragraph of paragraphs) {
    const p = trim(paragraph);
    if (!p) continue;
    let limit = sizeFor(chunks.length);
    const pieces = p.length > limit ? splitLongParagraph(p, limit, Math.max(limit, 400)) : [p];
    pieces.forEach((piece, n) => {
      limit = sizeFor(chunks.length);
      if (current.length + piece.length + 2 > limit && current) flush();
      current += current ? (n === 0 ? "\n\n" : " ") + piece : piece;
    });
  }
  flush();
  return chunks;
}

export const chunkArticle = (paragraphs: string[], skipped: number[] | null | undefined) => {
  const skip = new Set(skipped || []);
  return chunk(paragraphs.filter((_, i) => !skip.has(i)), progressiveSize);
};

export function segments(text: string, limit = SEGMENT_LIMIT): string[] {
  const pieces = chunk(text.split("\n\n"), () => limit);
  return pieces.length ? pieces : [text];
}

export function sentences(text: string): string[] {
  if (typeof Intl !== "undefined" && "Segmenter" in Intl) {
    const seg = new (Intl as any).Segmenter("en", { granularity: "sentence" });
    const out = Array.from(seg.segment(text), (s: any) => s.segment as string);
    return out.length ? out : [text];
  }
  return text.match(/[^.!?…]+[.!?…]+["'”’)\]]*\s*|[^.!?…]+$/g) ?? [text];
}

function splitLongParagraph(text: string, max: number, hard: number): string[] {
  const pieces: string[] = [];
  let current = "";
  for (const sentence of sentences(text)) {
    if (sentence.length > max) {
      if (current) { pieces.push(current); current = ""; }
      if (sentence.length > hard) pieces.push(...hardSplit(sentence, hard)); else pieces.push(sentence);
      continue;
    }
    if (current.length + sentence.length > max && current) { pieces.push(current); current = ""; }
    current += sentence;
  }
  if (current) pieces.push(current);
  return pieces.map((p) => p.replace(/^[ \t]+|[ \t]+$/g, ""));
}

function hardSplit(text: string, max: number): string[] {
  const result: string[] = [];
  let current = "";
  for (const word of text.split(" ").filter(Boolean)) {
    if (current.length + word.length + 1 > max && current) { result.push(current); current = ""; }
    current += current ? " " + word : word;
  }
  if (current) result.push(current);
  return result;
}

/** Splits text roughly in half at the nearest paragraph, else sentence, else word boundary. */
export function bisect(text: string): [string, string] | null {
  const target = Math.floor(text.length / 2);
  const split = (sep: string): [string, string] | null => {
    let best = -1, bestD = Infinity, from = 0;
    for (;;) {
      const i = text.indexOf(sep, from);
      if (i < 0) break;
      const end = i + sep.length;
      const d = Math.abs(end - target);
      if (d < bestD) { bestD = d; best = end; }
      from = end;
    }
    if (best < 0 || bestD >= Math.floor(target * 3 / 4)) return null;
    const a = text.slice(0, best).trim(), b = text.slice(best).trim();
    return a && b ? [a, b] : null;
  };
  return split("\n\n") ?? split(". ") ?? split(" ");
}
